import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { Video, Embed, toEmbedUrl } from './tiptap/MediaNodes';
import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Image as ImageIcon, Code, Quote, Minus,
  Undo2, Redo2, Eye, Code2, Video as VideoIcon, Youtube, Loader2
} from 'lucide-react';

const MenuButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active
        ? 'bg-[#ffd108]/20 text-[#ffd108]'
        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
    } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ content = '', onChange, placeholder = 'Start writing...', isDark = true, onMediaUpload = null }) => {
  const [mode, setMode] = useState('visual'); // visual | html | preview
  const [htmlSource, setHtmlSource] = useState('');
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable built-in extensions we configure manually
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#ffd108] underline' } }),
      Image.configure({ HTMLAttributes: { class: 'max-w-full rounded-lg my-2' } }),
      Video,
      Embed,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-3 ${
          isDark ? 'prose-invert text-slate-200' : 'text-gray-800'
        }`,
      },
    },
  });

  const switchToHtml = () => {
    if (editor) setHtmlSource(editor.getHTML());
    setMode('html');
  };

  const switchToVisual = () => {
    if (editor && htmlSource) {
      editor.commands.setContent(htmlSource);
      onChange?.(htmlSource);
    }
    setMode('visual');
  };

  const switchToPreview = () => setMode('preview');

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    if (onMediaUpload) {
      imageInputRef.current?.click();
    } else {
      const url = window.prompt('Enter image URL:');
      if (url && editor) editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleMediaFile = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onMediaUpload || !editor) return;
    setUploading(true);
    try {
      const url = await onMediaUpload(file);
      if (!url) throw new Error('No URL returned');
      if (kind === 'video') editor.chain().focus().setVideo({ src: url }).run();
      else editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const addVideoUpload = () => videoInputRef.current?.click();

  const addEmbed = () => {
    const url = window.prompt('Paste a YouTube or Vimeo link:');
    if (!url || !editor) return;
    const embedUrl = toEmbedUrl(url.trim());
    if (!embedUrl) {
      toast.error('Only YouTube and Vimeo links are supported');
      return;
    }
    editor.chain().focus().setEmbed({ src: embedUrl }).run();
  };

  if (!editor) return null;

  const currentHtml = mode === 'html' ? htmlSource : editor.getHTML();

  return (
    <div className={`rounded-lg border ${isDark ? 'border-slate-600 bg-slate-800/60' : 'border-gray-300 bg-white'}`}>
      {onMediaUpload && (
        <>
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleMediaFile(e, 'image')} data-testid="richtext-image-input" />
          <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleMediaFile(e, 'video')} data-testid="richtext-video-input" />
        </>
      )}
      {/* Mode tabs */}
      <div className={`flex items-center gap-1 px-2 py-1.5 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <Button
          type="button" variant="ghost" size="sm"
          className={`h-7 px-2.5 text-xs ${mode === 'visual' ? 'bg-[#ffd108]/15 text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-500'}`}
          onClick={() => { if (mode === 'html') switchToVisual(); else setMode('visual'); }}
        >
          <Bold className="h-3 w-3 mr-1" /> Visual
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className={`h-7 px-2.5 text-xs ${mode === 'html' ? 'bg-[#ffd108]/15 text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-500'}`}
          onClick={switchToHtml}
          data-testid="editor-html-mode"
        >
          <Code2 className="h-3 w-3 mr-1" /> HTML
        </Button>
        <Button
          type="button" variant="ghost" size="sm"
          className={`h-7 px-2.5 text-xs ${mode === 'preview' ? 'bg-[#ffd108]/15 text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-500'}`}
          onClick={switchToPreview}
          data-testid="editor-preview-mode"
        >
          <Eye className="h-3 w-3 mr-1" /> Preview
        </Button>
      </div>

      {/* Toolbar (visual mode only) */}
      {mode === 'visual' && (
        <div className={`flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <Bold className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <Italic className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <UnderlineIcon className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
            <Strikethrough className="h-4 w-4" />
          </MenuButton>

          <div className={`w-px h-5 mx-1 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />

          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
            <Heading1 className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
            <Heading2 className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
            <Heading3 className="h-4 w-4" />
          </MenuButton>

          <div className={`w-px h-5 mx-1 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />

          <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
            <List className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
            <ListOrdered className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
            <Quote className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">
            <Code className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
            <Minus className="h-4 w-4" />
          </MenuButton>

          <div className={`w-px h-5 mx-1 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />

          <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">
            <AlignLeft className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">
            <AlignCenter className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">
            <AlignRight className="h-4 w-4" />
          </MenuButton>

          <div className={`w-px h-5 mx-1 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />

          <MenuButton onClick={addLink} active={editor.isActive('link')} title="Insert Link">
            <LinkIcon className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={addImage} title="Insert Image" disabled={uploading}>
            <ImageIcon className="h-4 w-4" />
          </MenuButton>
          {onMediaUpload && (
            <>
              <MenuButton onClick={addVideoUpload} title="Upload Video" disabled={uploading}>
                <VideoIcon className="h-4 w-4" />
              </MenuButton>
              <MenuButton onClick={addEmbed} title="Embed YouTube/Vimeo">
                <Youtube className="h-4 w-4" />
              </MenuButton>
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-[#ffd108] ml-1" />}
            </>
          )}

          <div className="flex-1" />

          <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
            <Undo2 className="h-4 w-4" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
            <Redo2 className="h-4 w-4" />
          </MenuButton>
        </div>
      )}

      {/* Editor area */}
      {mode === 'visual' && (
        <EditorContent editor={editor} data-testid="richtext-editor" />
      )}

      {mode === 'html' && (
        <Textarea
          value={htmlSource}
          onChange={(e) => {
            setHtmlSource(e.target.value);
            onChange?.(e.target.value);
          }}
          placeholder="Paste or write HTML here..."
          className={`min-h-[250px] border-0 rounded-none font-mono text-sm resize-y ${
            isDark ? 'bg-slate-900 text-green-400' : 'bg-gray-50 text-gray-800'
          }`}
          data-testid="html-source-editor"
        />
      )}

      {mode === 'preview' && (
        <div
          className={`min-h-[200px] px-4 py-3 prose prose-sm max-w-none ${isDark ? 'prose-invert text-slate-200' : 'text-gray-800'}`}
          dangerouslySetInnerHTML={{ __html: currentHtml }}
          data-testid="editor-preview"
        />
      )}
    </div>
  );
};

export default RichTextEditor;
